/**
 * Custom Hook: AI Model Preferences
 * User Story 1: Configure AI Model Preferences
 * Task: T019
 */

import { useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { Id } from '../../convex/_generated/dataModel';
import { useCallback } from 'react';

/**
 * Hook for managing AI model preferences
 * Fetches preferences and provides mutation function
 */
export function useAiModelPreferences(businessId: Id<'businesses'> | undefined) {
  // Fetch preferences
  const preferences = useQuery(
    api.settings.aiPreferences.getAiModelPreferences,
    businessId ? { businessId } : 'skip'
  );

  // Update mutation
  const updateMutation = useMutation(api.settings.aiPreferences.updateAiModelPreferences);

  // Wrapper for update with businessId
  const updatePreferences = useCallback(
    async (params: {
      useClaude: boolean;
      useGPT: boolean;
      useGemini: boolean;
      claudeModel?: string;
      gptModel?: string;
      geminiModel?: string;
    }) => {
      if (!businessId) {
        throw new Error('Business ID is required');
      }

      return await updateMutation({
        businessId,
        ...params,
      });
    },
    [businessId, updateMutation]
  );

  // Calculate selected models count
  const selectedModelsCount =
    (preferences?.useClaude ? 1 : 0) +
    (preferences?.useGPT ? 1 : 0) +
    (preferences?.useGemini ? 1 : 0);

  return {
    preferences,
    isLoading: preferences === undefined,
    updatePreferences,
    isUpdating: false, // TODO: Track mutation state
    selectedModelsCount,
    hasPreferences: preferences !== null,
  };
}
